import type { ChannelConsolidatedSalesStoreItem } from "helpers/parsers/parseConsolidatedSales";
import { useCallback, useMemo } from "react";
import { fetchAnalyticsConsolidatedSales, getStoreKeyFromFilters } from "store/analyticsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TFetchStatus } from "store/types";
import type { AnalyticsFilters } from "types";

const getFetchedPages = <T>(pagesAsMap: Record<string, T>) => {
    return Object.keys(pagesAsMap)
        .map((str) => parseInt(str, 10))
        .filter(Boolean)
        .sort();
};

/**
 * This hook is used to fetch data for the channel consolidated sales table.
 */
const useChannelsSales = ({
    channelIds,
    titleIds: _titleIds,
    sortColumn,
    sortDirection,
    dates,
    maxParallelFetch,
}: {
    maxParallelFetch?: number;
} & AnalyticsFilters) => {
    const dispatch = useAppDispatch();

    const { analyticsConsolidatedSales, analyticsConsolidatedSalesStatus } = useAppSelector((state) => state.analytics);

    const titleIds = useMemo(() => {
        return _titleIds?.some?.((id) => id === "all") ? [] : _titleIds;
    }, [_titleIds]);

    const getFilters = useCallback(
        (channelId: number | string) => {
            return dates?.startDate || dates?.endDate
                ? ({
                      titleIds,
                      sortColumn,
                      sortDirection,
                      dates,
                      channelIds: [channelId],
                  } satisfies AnalyticsFilters)
                : null;
        },
        [titleIds, sortColumn, sortDirection, dates],
    );

    const fetchNextPage = useCallback(
        ({ channelsToFetch }: { channelsToFetch: string[] }) => {
            const channelsToIgnore = (channelIds || []).filter((channelId) => {
                const filters = getFilters(channelId);
                if (!filters) {
                    return false;
                }
                const storeKey = getStoreKeyFromFilters(filters);
                return ["pending", "failed", "rejected"].includes(analyticsConsolidatedSalesStatus[storeKey]);
            });

            const maxFetchableChannels = maxParallelFetch
                ? Math.max(0, maxParallelFetch - channelsToIgnore.length)
                : undefined;

            const fetchableChannels = channelsToFetch
                .filter((channelId) => {
                    const filters = getFilters(channelId);

                    if (!filters) {
                        return false;
                    }

                    const storeKey = getStoreKeyFromFilters(filters);

                    const isDone = analyticsConsolidatedSales[storeKey]?.status?.isDone;

                    return (
                        !isDone &&
                        (!analyticsConsolidatedSalesStatus[storeKey] ||
                            analyticsConsolidatedSalesStatus[storeKey] !== "pending")
                    );
                })
                .slice(0, maxFetchableChannels);

            Promise.all(
                fetchableChannels.map((channelId) => {
                    const filters = getFilters(channelId);

                    if (!filters) {
                        return;
                    }

                    const storeKey = getStoreKeyFromFilters(filters);

                    const currentTableData = analyticsConsolidatedSales[storeKey];

                    const lastFetchedPage = currentTableData?.status?.page;
                    const pageToFetch = lastFetchedPage ? lastFetchedPage + 1 : 1;

                    return dispatch(
                        fetchAnalyticsConsolidatedSales({
                            ...filters,
                            page: pageToFetch,
                            withTotal: pageToFetch === 1,
                            storeKey,
                        }),
                    );
                }),
            );
        },
        [
            channelIds,
            maxParallelFetch,
            analyticsConsolidatedSalesStatus,
            getFilters,
            analyticsConsolidatedSales,
            dispatch,
        ],
    );

    // Merge pages of data for each channel
    // and returns data ready to be displayed in the tables
    const mergedTableData = useMemo<
        Record<
            string,
            {
                data: ChannelConsolidatedSalesStoreItem[];
                rowOfTotals?: ChannelConsolidatedSalesStoreItem;
                isDone: boolean;
                fetchStatus?: TFetchStatus;
            }
        >
    >(() => {
        const storeKeys = (channelIds || [])
            .map((channelId) => {
                const filters = getFilters(channelId);

                if (!filters) {
                    return "";
                }

                return getStoreKeyFromFilters(filters);
            })
            .filter(Boolean);

        return storeKeys.reduce<
            Record<
                string,
                {
                    data: ChannelConsolidatedSalesStoreItem[];
                    rowOfTotals?: ChannelConsolidatedSalesStoreItem;
                    isDone: boolean;
                    fetchStatus?: TFetchStatus;
                }
            >
        >((acc, storeKey) => {
            const channelData = analyticsConsolidatedSales[storeKey];

            if (!channelData) {
                return acc;
            }

            const channelId = channelData.channelId;
            const pagesMap = channelData.pages;

            acc[channelId] = {
                isDone: !!channelData?.status?.isDone,
                fetchStatus: analyticsConsolidatedSalesStatus[storeKey],
                data: pagesMap
                    ? getFetchedPages(pagesMap)
                          .map((pageNum) => {
                              return pagesMap[pageNum];
                          })
                          .flat(1)
                    : [],
                rowOfTotals: channelData?.total,
            };
            return acc;
        }, {});
    }, [channelIds, getFilters, analyticsConsolidatedSales, analyticsConsolidatedSalesStatus]);

    return {
        fetchNextPage,
        tablesData: mergedTableData,
    };
};

export default useChannelsSales;
